/** Port von `IwmDialogBestellpositionAufteilen.razor`. */

import { useCallback, useEffect, useState } from 'react';

import { orderPositionApi } from '@/api/services/orderPositionApi';
import { Button, Dialog, NumericTextBox, ProgressButton } from '@/components/ui';
import type { BestellpositionDivideDefinition } from '@/types/definitions';

import { useDialogSource } from './useDialogSource';

export interface OrderPositionDivideDialogProps {
  open: boolean;
  onClose: () => void;
  viewId: number;
  orderPositionId: number;
  onSuccess: () => void;
}

/** Die berechneten Mengenfelder sind `Readonly` und melden keine Änderung zurück. */
function noop(): void {
  /* absichtlich leer */
}

export function OrderPositionDivideDialog(props: OrderPositionDivideDialogProps) {
  const { onClose, onSuccess, open, orderPositionId, viewId } = props;
  const [definition, setDefinition] = useState<BestellpositionDivideDefinition | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const source = useDialogSource(
    open,
    useCallback(
      () => orderPositionApi.getEntry(viewId, orderPositionId),
      [orderPositionId, viewId],
    ),
  );

  // Port von `New()`.
  useEffect(() => {
    if (!open) return;
    setDefinition({ SourceId: orderPositionId, Mengen: [0] });
    setErrorMessage(null);
    setSaving(false);
  }, [open, orderPositionId]);

  const sum =
    definition === null ? 0 : definition.Mengen.reduce((total, menge) => total + menge, 0);
  const exceeded = source !== null && sum > source.Menge;
  const valid =
    definition !== null && definition.Mengen.length > 0 && !exceeded && definition.SourceId > 0;

  const addMenge = () => {
    setDefinition((current) =>
      current === null ? current : { ...current, Mengen: [...current.Mengen, 0] },
    );
  };

  const updateMenge = (index: number, menge: number) => {
    setDefinition((current) =>
      current === null
        ? current
        : {
            ...current,
            Mengen: current.Mengen.map((value, position) => (position === index ? menge : value)),
          },
    );
  };

  const save = async () => {
    if (definition === null || !valid) return;
    setSaving(true);
    const result = await orderPositionApi.divide(viewId, definition);
    // Der Altclient lässt `Saving` im Fehlerfall stehen; hier würde das den Speichern-Knopf
    // dauerhaft sperren, deshalb wird das Kennzeichen in jedem Fall zurückgesetzt.
    setSaving(false);
    if (result.success) {
      setDefinition({ SourceId: 0, Mengen: [] });
      onClose();
      onSuccess();
    } else {
      setErrorMessage(result.message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width="650px"
      showCloseIcon
      isModal
      header="Divide order position"
    >
      {orderPositionId > 0 && source !== null && definition !== null ? (
        <>
          <div className="row">
            <div className="col-lg-12">
              <div className="row">
                <div className="col-lg-6">
                  <NumericTextBox
                    value={source.Menge}
                    onChange={noop}
                    readOnly
                    format="N0"
                    label="Menge (ursprünglich)"
                  />
                </div>
                <div className="col-lg-6">
                  <NumericTextBox
                    value={source.Menge - sum}
                    onChange={noop}
                    readOnly
                    format="N0"
                    label="Menge (nach Aufteilung)"
                  />
                </div>
              </div>
            </div>
            <div className="col-lg-12">
              {definition.Mengen.map((menge, index) => (
                <div className="row" key={index}>
                  <div className="col-lg-12">
                    <NumericTextBox
                      value={menge}
                      onChange={(value) => {
                        updateMenge(index, value ?? 0);
                      }}
                      format="N0"
                      label={`${String(index + 1)}. Teilmenge`}
                    />
                  </div>
                </div>
              ))}
            </div>
            {exceeded ? (
              <div className="col-lg-12">
                <div className="e-error">
                  Die Summe der Teilmengen übersteigt die Menge der ursprünglichen OrderPosition.
                </div>
              </div>
            ) : null}
            {errorMessage === null ? null : (
              <div className="col-lg-12">
                <div className="e-error">{errorMessage}</div>
              </div>
            )}
          </div>
          <div className="row" style={{ marginTop: '1rem', marginBottom: '1rem', float: 'right' }}>
            <div className="col-lg-12">
              <Button content="Teilmenge hinzufügen" onClick={addMenge} />
              <ProgressButton
                content="Save"
                primary
                type="submit"
                progress={saving}
                disabled={!valid}
                onClick={save}
              />
            </div>
          </div>
        </>
      ) : (
        <div className="e-error">Bitte wählen Sie eine OrderPosition aus</div>
      )}
    </Dialog>
  );
}

export default OrderPositionDivideDialog;
